import { useEffect, useState } from 'react';
import { useDispatch, connect } from "react-redux";
import { getFormValues } from 'redux-form';

import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

import { getFieldName, plainTypes, hasDisqualifyingAnswer } from "shared/utils";
import GenericField from 'shared/components/generic-field';

const QuestionSwitcher = (props) => {
  const {
    name,
    label,
    show,
    visibility,
    language,
    currentQuestion,
    setCurrentQuestion,
    steps,
    fieldState,
    formState,
    scenario
  } = props;

  const dispatch = useDispatch();

  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(true);
  const [history, setHistory] = useState();

  useEffect(() => {
    setHistory([...steps]);
  }, []);

  let now = new Date();
  let id = Math.round(now.getTime() / 1000);

  // Ключ из ID шагов — меняется когда состав видимых вопросов изменяется
  const stepsIds = steps.map(s => s.id).join(',');

  // Кнопка "Вперёд": пересчитываем при смене вопроса И при изменении ответа
  useEffect(() => {
    setNextDisabled(!hasNextQuestion());
  }, [currentQuestion, fieldState]);

  // Синхронизируем history когда steps расширяется (например Q1 появляется после ответа на Q2).
  // Без этого onNextIndexHandler берёт history[nextStepIndex] = undefined → сброс на Q0.
  useEffect(() => {
    if (!history) return;
    const newHistory = steps.map(step => {
      const h = history.find(h => h.id === step.id);
      return h ? { ...step, attributes: h.attributes } : { ...step };
    });
    setHistory(newHistory);
  }, [stepsIds]);

  useEffect(() => {
    setPrevDisabled(!hasPrevQuestion());

    if (history) {
      const newHistory = steps.map(step => {
        const localHistoryElement = history.find(h => h.id === step.id);

        if (localHistoryElement) {
          return {
            ...step,
            attributes: localHistoryElement.attributes
          }
        }

        return {
          ...step
        }
      });

      setHistory(newHistory);
    } else {
      setHistory([...steps]);
    }


    if (fieldState?.result) {
      const data = {
        ...scenario,
        [currentQuestion.id]: true
      }
      dispatch({
        type: 'survey/setScenario',
        payload: data,
      });
    } else {
      const data = {
        ...scenario
      }
      delete data[currentQuestion.id];
      dispatch({
        type: 'survey/setScenario',
        payload: data,
      });
    }
  }, [currentQuestion]);

  // Getting subscriber's question answer from fieldState(redux-form store)
  // if it was previously answered
  const getQuestionAnswer = (currentStepIndex) => {
    // console.log('getQuestionAnswer fieldState', fieldState);
    let newAttributes;
    if (fieldState) {
      // if just change one value to another
      if (Object.keys(fieldState).length > 0) {
        if (plainTypes.includes(fieldState?.typeName)) {
          // --- This is similar to onNextIndexHandler method code chunk --<<<<<<<<<<<<<<<<< START
          // ToDo: Both code should be refactored into one method
          const isDefaultValue = (!currentQuestion.attributes || currentQuestion.attributes.length < 1)
              ? false
              : currentQuestion.attributes.findIndex(a => a.default &&
              (
                  (a.typeName === 'OPTION' || a.typeName === 'CUSTOM_OPTION')
              )
          ) > -1;

          // console.log('isDefaultValue', isDefaultValue, currentQuestion);

          if (!isDefaultValue) {
            newAttributes = currentQuestion?.attributes ? [...currentQuestion.attributes] : [];
            const localId = ++id;
            // ToDo: Check for CUSTOM_OPTION/ may need to add for typeId, typeName for corresponding CUSTOM_OPTION
            newAttributes.push(
                {
                  default: true,
                  id: localId,
                  label: null,
                  name: `localId-${localId}`,
                  order: 0,
                  typeId: 0,
                  typeName: "OPTION",
                  value: fieldState.result
                }
            );
          }
          else {
            newAttributes = currentQuestion.attributes.map(a => {
              if (a.default && (a.typeName === 'OPTION' || a.typeName === 'CUSTOM_OPTION')) {
                return {
                  ...a,
                  value: fieldState.result
                }
              }
              return {
                ...a
              }
            });
          }
          // --- This is similar to onNextIndexHandler method code chunk -->>>>>>>>>>>>>>>> END
        }
        else {
          newAttributes = currentQuestion.attributes?.reduce((filtered, attribute) => {
            // Other types always have OPTIONs since they are set of available answers just set\unset default property
            if (fieldState?.typeName === 'PERIOD') {
              if (attribute.typeName === 'PERIOD-START') {
                filtered.push({
                  ...attribute,
                  default: true,
                  value: fieldState.result.start
                });
              }
              if (attribute.typeName === 'PERIOD-END') {
                filtered.push({
                  ...attribute,
                  default: true,
                  value: fieldState.result.end
                });
              }
            }
            // ToDo: Check for multiple! one of 'SELECT'(multiple), 'CHECKBOX' may hold multiple answers
            if (['SELECT', 'COMBOBOX', 'RADIO'].includes(fieldState?.typeName)) {
              // console.log('RADIO getQuestionAnswer', fieldState?.typeName, fieldState, attribute);
              if (attribute.typeName === 'OPTION' || (fieldState.typeName === 'RADIO' && attribute.typeName === 'CUSTOM_OPTION')) {
                filtered.push({
                  ...attribute,
                  default: attribute.id === fieldState.result.id ? true : false,
                  value: attribute.typeName === 'CUSTOM_OPTION' ? fieldState.result.value : attribute.value
                });
              }
            }
            // ToDo: Check for multiple! one of 'SELECT'(multiple), 'CHECKBOX' may hold multiple answers
            if (['CHECKBOX', 'MULTI-INPUT'].includes(fieldState?.typeName)) {
              // console.log('CHECKBOX getQuestionAnswer', fieldState?.typeName, fieldState, attribute);
              // if (attribute.typeName === 'OPTION') {
              if (attribute.typeName === 'OPTION' || attribute.typeName === 'CUSTOM_OPTION' || attribute.typeName === 'CANCEL_OPTION') {
                const optionState = fieldState.result.find(opt => opt.id === attribute.id);
                // console.log('CHECKBOX getQuestionAnswer optionState', optionState);
                filtered.push({
                  ...attribute,
                  default: !!optionState,
                  value: attribute.typeName === 'CUSTOM_OPTION' ? optionState?.value : attribute.value
                });
                // console.log('CHECKBOX getQuestionAnswer filtered', JSON.parse(JSON.stringify(filtered)));
              }
            }
            if (fieldState?.typeName === 'IMAGE-SELECT') { // ToDo: add also for FILE
              if(fieldState.multiple){
                const optionState = fieldState.result.find(opt => opt.id === attribute.id);
                filtered.push({
                  ...attribute,
                  default: !!optionState,
                });
              }else{
                filtered.push({
                  ...attribute,
                  default: attribute.id === fieldState.result[0].id ? true : false,
                });
              }
              // skipping current attribute if it's not in state for filter method
              // return;
            }
            // For other data types
            return filtered;
            // return { ...attribute }
          }, []);

          // For cases when no attributes(default value) exists then array is empty
          newAttributes = !newAttributes ? [] : newAttributes;

          if (newAttributes.length < 1) {
            if (fieldState?.typeName === 'PERIOD') {
              let attribute;
              if (fieldState.result.start) {
                const localId = ++id;
                attribute = {
                  default: true,
                  id: localId,
                  label: null,
                  name: `localId-${localId}`,
                  order: 0,
                  typeId: 5,
                  typeName: "PERIOD-START",
                  value: fieldState.result.start
                }
                newAttributes.push(attribute);
              }
              if (fieldState.result.start) {
                const localId = ++id;
                attribute = {
                  default: true,
                  id: localId,
                  label: null,
                  name: `localId-${localId}`,
                  order: 0,
                  typeId: 6,
                  typeName: "PERIOD-END",
                  value: fieldState.result.end
                }
                newAttributes.push(attribute);
              }
            }
          }
        }
      } else {
        // console.log('1 fieldState', fieldState)
        newAttributes = currentQuestion.attributes ? [...currentQuestion.attributes] : [];
        // if had a default value which was removed and current value is null, then remove from attributes
        if (currentQuestion?.attributes && currentQuestion?.attributes?.length > 0) {
          newAttributes = currentQuestion.attributes.reduce((filtered, attribute) => {
            if (attribute.typeName === 'BLOB') {
              return filtered;
            }
            if (['PERIOD-START', 'PERIOD-END'].includes(attribute.typeName)) {
              return filtered;
            }
            filtered.push({ ...attribute, default: false })

            return filtered;
          }, []);
        }
      }
    } else {
      // console.log('2 fieldState', fieldState)
      newAttributes = currentQuestion.attributes ? [...currentQuestion.attributes] : [];
      // if had a default value which was removed and current value is null, then remove from attributes
      if (currentQuestion?.attributes && currentQuestion?.attributes?.length > 0) {
        newAttributes = currentQuestion.attributes.reduce((filtered, attribute) => {
          if (attribute.typeName === 'BLOB') {
            return filtered;
          }
          if (['PERIOD-START', 'PERIOD-END'].includes(attribute.typeName)) {
            return filtered;
          }
          filtered.push({ ...attribute, default: false })

          return filtered;
        }, []);
      }
    }

    const historyElement = {
      ...history[currentStepIndex],
      attributes: newAttributes
    }
    const newHistory = [...history];
    newHistory[currentStepIndex] = historyElement;

    // console.log('getQuestionAnswer newAttributes', newAttributes);

    return newHistory;
  }

  const onPrevIndexHandler = (event) => {
    const currentStepIndex = history.findIndex(q => q.id === currentQuestion.id);
    const prevStepIndex = currentStepIndex - 1;

    const data = {
      ...scenario
    }
    delete data[currentQuestion.id];
    // console.log('4 setAnswer', currentQuestion.id, scenario, data);
    dispatch({
      type: 'survey/setScenario',
      payload: data,
    });

    if (currentStepIndex > 0) {
      // console.log('onPrevIndexHandler history[prevStepIndex]', history[prevStepIndex]);
      // console.log('onPrevIndexHandler fieldState', fieldState, prevStepIndex, currentStepIndex);
      if (prevStepIndex < currentStepIndex) {
        const newHistory = getQuestionAnswer(currentStepIndex);
        setHistory(newHistory);
      }
      setCurrentQuestion(history[prevStepIndex])
    }
  }

  // Setting Next Question Answers from history, Form-State. Rewrites default values by values user input earlier
  const onNextIndexHandler = (event) => {
    const lastStepIndex = history.length - 1;
    const currentStepIndex = history.findIndex(q => q.id === currentQuestion.id);
    const nextStepIndex = currentStepIndex + 1;

    // console.log('onNextIndexHandler currentStepIndex', currentStepIndex, 'nextStepIndex', nextStepIndex);

    // console.log('2 onNextIndexHandler nextStepIndex', nextStepIndex, 'lastStepIndex', lastStepIndex, nextStepIndex <= lastStepIndex);

    if (nextStepIndex <= lastStepIndex) {
      const newHistory = getQuestionAnswer(currentStepIndex);
      // console.log('3 onNextIndexHandler newHistory', newHistory ? JSON.parse(JSON.stringify(newHistory)) : null);
      setHistory(newHistory);
    }
    // console.log('4 onNextIndexHandler formState', formState, nextStepIndex);
    // console.log('4 onNextIndexHandler next', history ? JSON.parse(JSON.stringify(history)) : null);

    // nextStep fieldState must be provided or map to props directly from redux-from store(all answers, not only current)
    const question = history[nextStepIndex];

    // console.log('question', question, history);

    if (question) {


      const nextFieldState = formState?.[getFieldName(question.name)]

      if (nextFieldState) {
        // console.log('5 onNextIndexHandler nextFieldState', nextFieldState, question)

        if (nextFieldState.id === question.id) {
          if (plainTypes.includes(question.typeName)) {
            // console.log('6 onNextIndexHandler isDefaultValue', question.typeName);
            let newAttributes;
            // --- This is similar to getQuestionAnswer method code chunk --<<<<<<<<<<<<<<<<< START
            // ToDo: Both code should be refactored into one method
            // ToDo: Test default OPTIONs for other types of Component(text, number, date, etc)
            // ToDo: Test without any default OPTIONs for other types of Component(text, number, date, etc)
            // ToDo: Test with NO ATTRIBUTE for other types of Component(text, number, date, etc)
            // Note: newAttributes is assigned to question.attributes in the end of this chunk,
            //       but getQuestionAnswer is in the very end(out of the chunk)
            //       So, new Method returns result and result should be assigned to newAttributes
            //       and the newAttributes to question.attributes;
            // Note: Why in getQuestionAnswer for NON Plain Components result\value is calculated in other way in onNextIndexHandler
            const isDefaultValue = (!question.attributes || question.attributes.length < 1)
                ? false
                : question.attributes.findIndex(a => a.default &&
                (
                    (a.typeName === 'OPTION' || a.typeName === 'CUSTOM_OPTION')
                )
            ) > -1;

            // console.log('7 onNextIndexHandler isDefaultValue', isDefaultValue, question);

            if (!isDefaultValue) {
              newAttributes = question?.attributes ? [...question.attributes] : [];
              const localId = ++id;
              // ToDo: Check for CUSTOM_OPTION/ may need to add for typeId, typeName for corresponding CUSTOM_OPTION
              newAttributes.push(
                  {
                    default: true,
                    id: localId,
                    label: null,
                    name: `localId-${localId}`,
                    order: 0,
                    typeId: 0,
                    typeName: "OPTION",
                    value: nextFieldState.result
                  }
              );
              question.attributes = newAttributes;
            } else {
              newAttributes = question.attributes.map(a => {
                if (a.default && (a.typeName === 'OPTION' || a.typeName === 'CUSTOM_OPTION')) {
                  return {
                    ...a,
                    value: nextFieldState.result
                  }
                }
                return {
                  ...a
                }
              });
              question.attributes = newAttributes;
            }

          }
          else if (("SELECT" === question.typeName && question.multiple === true)
              || ['CHECKBOX', 'MULTI-INPUT', 'IMAGE-SELECT'].includes(question.typeName)
          ) {

            // console.log('9 onNextIndexHandler NON-Plain Type nextFieldState', nextFieldState, question)

            if (nextFieldState?.result?.length > 0) {

              const attributes = question.attributes.map(a => {
                const matchedIndex = nextFieldState.result.findIndex(option => option.id === a.id);

                return {
                  ...a,
                  default: matchedIndex > -1 ? true : false
                }

              });

              question.attributes = attributes;

            }

          } else {
            // console.log('10 onNextIndexHandler Other types typeName', question.typeName, nextFieldState?.result)
            if (nextFieldState?.result) {
              const attributes = question?.attributes ? question.attributes.map(a => {
                // console.log('a', a)
                if (a.id == nextFieldState.result.id) {
                  return {
                    ...a,
                    default: true
                  }
                }
                return { ...a }
              }) : question?.attributes;

              // console.log('onNextIndexHandler attributes', attributes);
              question.attributes = attributes;
            }
          }
        }
      }
    }

    return setCurrentQuestion(question);

    // return setCurrentQuestion(history[nextStepIndex]);
  }

  const hasPrevQuestion = () => {
    if (!steps || steps.length < 2) {
      return false;
    }
    const index = steps.findIndex(step => step.id === currentQuestion.id);
    if (index < 1) {
      return false;
    }
    return true;
  }

  // Enable/Disable Next button
  const hasNextQuestion = () => {
    let value = fieldState && Object.keys(fieldState)?.length ? { ...fieldState } : null;

    // console.log('-----------------------------------------');
    // console.log('hasNextQuestion', currentQuestion?.id, currentQuestion?.label?.ru, fieldState, value);

    // PERIOD Type consists of 2 parts: start and end dates
    if (value?.result && currentQuestion.typeName === 'PERIOD') {
      // Check if start and end dates are filled, if not then it's null
      Object.keys(value?.result).forEach((val) => {
        if (value?.result[val] == null) {
          value = null;
        }
      });
    }

    // If the selected option disqualifies the respondent, there is no next
    // question — the survey must end here.
    if (value && hasDisqualifyingAnswer(value)) {
      return false;
    }

    // console.log('hasNextQuestion nextQuestions', currentQuestion?.nextQuestions);
    // If it's mandatory/required and has no answer selected
    if (!value && currentQuestion?.required) {
      return false;
    }

    // If current question is mandatory/required and has answer selected
    // if it's radio then it can have CUSTOM OPTION answer then
    // Check for the value in the Text Input
    if (value && currentQuestion?.required) {
      // console.log('Is Next', currentQuestion, value);
      if (currentQuestion.typeName === 'RADIO') {
        if (value?.result?.typeName === 'CUSTOM_OPTION' && !value?.result?.value) {
          // console.log('Is Next', currentQuestion, value);
          return false;
        }
      }

      if (['CHECKBOX', 'MULTI-INPUT'].includes(currentQuestion.typeName)) {
        // console.log('Is Next', currentQuestion, value);
        if (value?.result?.some(v => v.typeName === 'CUSTOM_OPTION' && !v.value)) {
          return false;
        }
      }
    }

    if (currentQuestion?.nextQuestions) {
      // if there is no dependency, at least there is one explicitly given next/child question then it has next question
      const nextQuestion = currentQuestion.nextQuestions.find(child => child.expectedValues == null || child.expectedValues.length < 1);

      // console.log('hasNextQuestion nextQuestion', nextQuestion);

      if (nextQuestion) {
        return true;
      }

      if (value) {
        for (let i = 0; i < currentQuestion.nextQuestions.length; i++) {
          const expectedValuesCount = currentQuestion.nextQuestions[i].expectedValues?.length;

          // console.log('!!1', '------------------------------------------');
          // console.log('!!1', currentQuestion.nextQuestions[i].showOn, currentQuestion.id);

          for (let j = 0; j < currentQuestion.nextQuestions[i].expectedValues.length; j++) {

            // console.log('!!1', i, j, currentQuestion.nextQuestions[i].showOn, currentQuestion.nextQuestions[i].id, currentQuestion.nextQuestions[i].expectedValues[j])
            if (plainTypes.includes(currentQuestion.typeName)) {
              if (currentQuestion.nextQuestions[i].expectedValues[j]?.value === value?.result) {
                return true;
              }
            } else if (("SELECT" === currentQuestion.typeName && currentQuestion.multiple === true)
                || ['CHECKBOX', 'MULTI-INPUT'].includes(currentQuestion.typeName)) {

              if (!currentQuestion.nextQuestions[i].showOn
                  || currentQuestion.nextQuestions[i].showOn === 'EQUAL'
                  || currentQuestion.nextQuestions[i].showOn === 'IN'
              ) {

                // console.log('!!1', currentQuestion.nextQuestions[i].showOn, value.result?.length, expectedValuesCount);

                if (value.result?.length > 1) {
                  // console.log('!!1', currentQuestion.nextQuestions[i].showOn, value.result?.length, 'break 1');
                  break; //return false;
                }
                // Compare ONE answer to expected values(more than ONE value), 
                // IN compares One answer to all elements of possible/expected answers till the first satisfaction


                if (!currentQuestion.nextQuestions[i].showOn || currentQuestion.nextQuestions[i].showOn === 'EQUAL') {
                  if (value.result?.length !== expectedValuesCount) {
                    // console.log('!!1', currentQuestion.nextQuestions[i].showOn, value.result?.length, expectedValuesCount, 'break 2');
                    break; //return false;
                  }
                }
                // console.log('!!2 hasNextQuestion', value?.result?.length)
                if (value?.result?.length > 0) {
                  const matchedIndex = value.result.findIndex(option => option.id === currentQuestion.nextQuestions[i]?.expectedValues[j]?.id);

                  // console.log('!!1', currentQuestion.nextQuestions[i].showOn, matchedIndex, currentQuestion.nextQuestions[i]?.expectedValues[j]?.id);

                  if (matchedIndex > -1) {
                    // console.log('!!1', currentQuestion.nextQuestions[i].showOn, value.result?.length, expectedValuesCount, 'true');
                    return true;
                  } else {
                    // console.log(currentQuestion.nextQuestions[i].showOn, 'matchedIndex = -1');
                    // return false;
                  }
                  // console.log('!!4', visibility, resultMatch[nextQuestionId]);
                }

              } else if (currentQuestion.nextQuestions[i].showOn === 'ANY') {
                // console.log('!!1', currentQuestion.nextQuestions[i].showOn, value.result, expectedValuesCount)
                if (value?.result?.length > 0) {
                  const matchedIndex = value.result.findIndex(option => option.id === currentQuestion.nextQuestions[i]?.expectedValues[j]?.id);;
                  // if one collection matches to another
                  if (matchedIndex > -1) {
                    // console.log('!!1', currentQuestion.nextQuestions[i].showOn, value.result[matchedIndex])
                    return true
                  }
                }

              } else if (currentQuestion.nextQuestions[i].showOn === 'ALL') {
                // if answer.result collection's ALL elements satisfy for equity to ALL elements of nextQuestions[i]?.expectedValues
                // console.log('!', currentQuestion.nextQuestions[i].showOn, value.result, currentQuestion.nextQuestions[i].expectedValues, value.result?.length, expectedValuesCount)
                if (value.result?.length !== expectedValuesCount) {
                  // console.log('False 1');
                  break; //return false;
                }
                const matchedIndex = value.result.findIndex(option => option.id === currentQuestion.nextQuestions[i]?.expectedValues[j]?.id);;
                // if one collection matches to another
                if (matchedIndex < 0) {
                  // console.log('False 2');
                  break; //return false;
                }
                // If it's last value to compare to answers list and yet there is no unsatisfied condition
                // if (matchedIndex > -1 && j === expectedValuesCount - 1) {
                if (j === expectedValuesCount - 1) {
                  // console.log('True 1');
                  return true;
                }
                // console.log('end')
              }
            } else {
              if (currentQuestion.nextQuestions[i].expectedValues[j]?.id === value?.result?.id) {
                return true;
              }
            }
          }
        }
        // console.log('!2', 'RETURN FALSE 1')
        return false;
      }
    }
    // console.log('!3', 'RETURN FALSE 2')
    return false;
  }

  const { t } = useTranslation();

  if (show) return null;

  return (
      <div style={{
        display:'flex',
        flexDirection:'column',
        height:'100%'
      }}>
        <GenericField
            {...props}
            attributes={currentQuestion?.attributes || []}
            media={currentQuestion?.media || []}
            name={name}
            label={label}
            visibility={visibility}
            language={language}
            fieldState={fieldState}
        />
        <div
            style={{
              display:'flex',
              justifyContent:'center',
              gap:'10px',
              flex:1,
              alignItems:'flex-end',
              marginBottom:'1em'
            }}
        >
          <Button
              variant='contained'
              disabled={prevDisabled}
              onClick={onPrevIndexHandler}
              style={{
                width:'250px',
                height:'40px'
              }}
          >
            <ArrowBack style={{ marginTop: -3, marginRight: 5, width:'20px'}} />
            {' '}
            {t('previous')}
          </Button>
          <Button
              variant='contained'
              disabled={nextDisabled}
              onClick={onNextIndexHandler}
              style={{
                width:'250px',
                height:'40px'
              }}
          >
            {t('next')}
            {' '}
            <ArrowForward fontSize='16' style={{ marginTop: -3, marginLeft: 5, width:'20px'}} />
          </Button>
        </div>
      </div>
  )
}

const mapStateToProps = state => {
  const formState = getFormValues('survey')(state);
  return {
    formState,
    scenario: state.survey.scenario
  }
};

// const mapDispatchToProps = {
//   change
// };

const QuestionSwitcherConnected = connect(
    mapStateToProps,
    null //mapDispatchToProps
)(QuestionSwitcher)

// export default QuestionSwitcher;
export default QuestionSwitcherConnected;